import { Anchor, Box, Container, Group, Image, Paper, Progress, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { BrandName } from '../shared/BrandName';

const PUBLIC_SITE_URL = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://thegameproject.net';

interface OnboardingShellProps {
  children: ReactNode;
  step?: 1 | 2;
  stageLabel?: string;
  reassurance: string;
}

export default function OnboardingShell({ children, step, stageLabel, reassurance }: OnboardingShellProps) {
  return (
    <Box
      mih="100dvh"
      py={{ base: 28, sm: 48 }}
      style={{
        background:
          'radial-gradient(circle at 15% 10%, rgba(53, 214, 255, 0.2), transparent 32rem), radial-gradient(circle at 90% 88%, rgba(8, 127, 231, 0.24), transparent 34rem), #0a1630',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        aria-hidden="true"
        pos="absolute"
        inset={0}
        opacity={0.22}
        style={{
          backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.28) 0.65px, transparent 0.65px)',
          backgroundSize: '9px 9px',
          pointerEvents: 'none',
        }}
      />

      <Container size={680} px="md" pos="relative" w="100%">
        <Stack gap="xl">
          <Group justify="center" gap="sm" wrap="nowrap">
            <Image src="/data/logo.svg" alt="The Game Project" w={52} h={52} fit="contain" />
            <BrandName size="large" color="#ffffff" />
          </Group>

          <Stack gap={8} maw={560} w="100%" mx="auto">
            <Group justify="space-between" gap="md">
              <Text c="cyan.2" size="xs" fw={700} tt="uppercase" lts="0.12em">
                Account setup
              </Text>
              <Text c="gray.3" size="sm">
                {stageLabel || `Step ${step} of 2`}
              </Text>
            </Group>
            {step && (
              <Progress
                value={step * 50}
                size={6}
                radius="xl"
                color="cyan.4"
                bg="rgba(255, 255, 255, 0.14)"
                aria-label={`Account setup: step ${step} of 2`}
              />
            )}
          </Stack>

          <Paper
            maw={620}
            w="100%"
            mx="auto"
            p={{ base: 'lg', sm: 36 }}
            radius="lg"
            shadow="xl"
            style={{ borderTop: '5px solid #ff7a45' }}
          >
            {children}
          </Paper>

          <Stack align="center" gap="sm">
            <Text c="gray.3" size="sm" ta="center">
              {reassurance}
            </Text>
            <Group gap="lg" justify="center">
              <Anchor c="gray.4" size="xs" href={`${PUBLIC_SITE_URL}/support`} target="_blank">
                Support
              </Anchor>
              <Anchor c="gray.4" size="xs" href={`${PUBLIC_SITE_URL}/terms`} target="_blank">
                Terms
              </Anchor>
              <Anchor c="gray.4" size="xs" href={`${PUBLIC_SITE_URL}/privacy`} target="_blank">
                Privacy
              </Anchor>
            </Group>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
